import {
  Alert,
  Button,
  ConfigProvider,
  Dropdown,
  Input,
  Modal,
  Tooltip
} from "antd";
import { apiRequest, createAntdFilter, useDatadog } from "@vojomo-core/shared";
import { useAccount } from "@vojomo-core/shared";
import { useEffect, useMemo, useState, useRef } from "react";
import { getFriendlyTimeAgo } from "../../../utils/helpers/time";
import Table, { ColumnsType } from "antd/es/table";
import { FaEllipsisVertical } from "react-icons/fa6";
import { useCreateNote, CreateNoteModal } from "../../../components/CreateNote";
import { ShareAccessModal } from "../../../pages/TopicViewer/components/ShareAccessModal";
import { useSource } from "../../../pages/TopicViewer/hooks/use-sources";

interface DataType {
  _id: string;
  updated_at: Date;
  owner: string;
  file_name: string;
  file_type: string;
  key: number;
}

export function ManageSources(): React.ReactElement {
  const { companyName, vojomoAccount } = useAccount();
  const { trackEvent, trackError } = useDatadog();
  const [gcsPath, setGcsPath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sameNameModalOpen, setSameNameModalOpen] = useState(false);
  const { sources: originalSources, fetchSources } = useSource();

  const [sameHashModalOpen, setSameHashModalOpen] = useState(false);
  const [sameHashModalFiles, setSameHashModalFiles] = useState<any[]>([]);

  const [ignoreSameHash, setIgnoreSameHash] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState<string | null>(null);
  const [isNewFileNameTaken, setIsNewFileNameTaken] = useState<boolean>(false);

  const [file, setFile] = useState<File | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isShareModalVisible, setIsShareModalVisible] =
    useState<boolean>(false);

  const [selectedSource, setSelectedSource] = useState<{
    name: string;
    id: string;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Ref for the debounce timeout
  const checkFileNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiRequest({
        subRoute: "ai",
        method: "POST",
        path: `/delete_source`,
        body: {
          sourceId: selectedSource?.id
        }
      });
      await fetchSources();
    } catch (error) {
      trackError("error_deleting_source", error);
      setErrorMessage("Failed to delete source. Please try again.");
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  const handleBulkDelete = async () => {
    setDeleteLoading(true);
    try {
      // Delete all selected sources
      await Promise.all(
        selectedRowKeys.map((key) => {
          const record = sources?.find((row) => row.key === key);
          if (record) {
            return apiRequest({
              subRoute: "ai",
              method: "POST",
              path: `/delete_source`,
              body: {
                sourceId: record._id
              }
            });
          }
          return Promise.resolve();
        })
      );
      await fetchSources();
      setSelectedRowKeys([]);
    } catch (error) {
      trackError("error_deleting_sources", error);
      setErrorMessage("Failed to delete sources. Please try again.");
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalVisible(false);
    }
  };

  async function getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      ...createAntdFilter<DataType>("updated_at", "Updated"),
      sorter: (a: any, b: any) =>
        a.updated_at.getTime() - b.updated_at.getTime(),
      render: (_: any, record: any) => getFriendlyTimeAgo(record.updated_at)
    },

    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
      sorter: (a: any, b: any) => a.file_name.localeCompare(b.file_name),
      ...createAntdFilter<DataType>("file_name", "File Name")
    },
    {
      title: "Type",
      dataIndex: "file_type",
      key: "file_type",
      ...createAntdFilter<DataType>("file_type", "File Type"),
      render: (_: any, record: any) => {
        const fileType = record.file_type
          .replace("application/", "")
          .replace("text/", "");

        if (fileType.startsWith("topic/")) {
          let topicFmt = fileType.replace("topic/", "");
          topicFmt = topicFmt.replace("building-block", "Building Block");
          topicFmt = topicFmt.replace("briefing-doc", "Alignment Report");
          return topicFmt;
        }
        return fileType;
      }
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: 50,
      render: (_: any, record: any) => {
        if (record.owner !== vojomoAccount["_id"]) {
          return null;
        }

        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "delete",
                  label: "Delete",
                  onClick: () => {
                    setSelectedSource({
                      id: record._id,
                      name: record.file_name
                    });
                    setIsDeleteModalVisible(true);
                  }
                },
                {
                  key: "share",
                  label: "Share",
                  onClick: () => {
                    setSelectedSource({
                      id: record._id,
                      name: record.file_name
                    });
                    setIsShareModalVisible(true);
                  }
                }
              ]
            }}
          >
            <FaEllipsisVertical className="cursor-pointer" />
          </Dropdown>
        );
      }
    }
  ];

  const [sources, setSources] = useState<Array<DataType> | null>(null);

  const filteredData = useMemo(() => {
    if (!sources || !searchTerm) return sources;

    const lowerSearch = searchTerm.toLowerCase();
    return sources.filter((row) => {
      const title = (row.file_name || "").toLowerCase();
      const type = (row.file_type || "").toLowerCase();
      return title.includes(lowerSearch) || type.includes(lowerSearch);
    });
  }, [sources, searchTerm]);

  const ownedSources = useMemo(() => {
    if (!filteredData) {
      return null;
    }

    return filteredData.filter(
      (source) => source.owner === vojomoAccount["_id"]
    );
  }, [filteredData]);

  const sharedSources = useMemo(() => {
    if (!filteredData) {
      return null;
    }

    return filteredData.filter(
      (source) => source.owner !== vojomoAccount["_id"]
    );
  }, [filteredData]);

  // Effect to auto-close alerts after 15 seconds
  useEffect(() => {
    if (errorMessage || gcsPath) {
      const timer = setTimeout(() => {
        if (errorMessage) setErrorMessage(null);
        if (gcsPath) {
          setGcsPath(null);
          fetchSources();
        }
      }, 15000); // 15 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage, gcsPath]);

  // Refetch sources when company changes
  useEffect(() => {
    fetchSources();
  }, [companyName]);

  useEffect(() => {
    const rowData = originalSources
      .filter((s) => !s.file_type.includes("topic/"))
      .map((source: any, i: number) => {
        return {
          _id: source._id,
          updated_at: new Date(source.updated_at),
          file_name: source.file_name,
          file_type: source.file_type,
          owner: source.owner,
          key: i
        };
      });
    setSources(rowData);
  }, [originalSources]);

  // Create note hook
  const createNoteHook = useCreateNote({
    onNoteCreated: async () => {
      await fetchSources();
    }
  });

  // Check if a filename already exists in the database
  const checkFileNameExists = async (fileName: string): Promise<boolean> => {
    try {
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileName)}`,
        method: "GET"
      });

      return check_file_name.length > 0;
    } catch (error) {
      trackError("error_checking_file_name", error, { fileName });
      return false; // Assume it doesn't exist on error to allow user to try upload
    }
  };

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    const parts = filename.split(".");
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
  };

  // Helper function to ensure filename has the correct extension
  const ensureFileExtension = (
    newName: string,
    originalName: string
  ): string => {
    const originalExt = getFileExtension(originalName);
    // If new name already has the correct extension, return as is
    if (newName.endsWith(originalExt)) {
      return newName;
    }

    // Remove any extension from new name and add the original extension
    const newNameWithoutExt = newName.includes(".")
      ? newName.substring(0, newName.lastIndexOf("."))
      : newName;

    return `${newNameWithoutExt}${originalExt}`;
  };
  const handleRenameAndUpload = async () => {
    if (!file || !newFileName) {
      setErrorMessage("File or new name is missing");
      return;
    }

    const correctedFileName = ensureFileExtension(newFileName, file.name);

    setIsLoading(true);

    try {
      // Check if the new filename already exists
      const fileNameExists = await checkFileNameExists(correctedFileName);

      if (fileNameExists) {
        setIsNewFileNameTaken(true);
        setErrorMessage(
          `The name "${correctedFileName}" is already taken. Please choose another name.`
        );
        setIsLoading(false);
        return;
      }

      // Create a new file with the new name (with correct extension) but same content
      const renamedFile = new File([file], correctedFileName, {
        type: file.type
      });
      const base64_file = await getBase64(renamedFile);

      const data = await apiRequest<{
        gcs_path: string;
        inserted_id: string;
      }>({
        subRoute: "ai",
        path: `/upload_file`,
        method: "POST",
        body: {
          base64_file: base64_file,
          file_type: renamedFile.type,
          file_name: renamedFile.name
        }
      });

      setGcsPath(data.gcs_path);
      setSameNameModalOpen(false);
      setNewFileName(null);
      setIsNewFileNameTaken(false);
      fetchSources();
    } catch (error) {
      trackError("error_uploading_renamed_file", error, {
        oldName: file.name,
        newName: newFileName
      });

      setErrorMessage("Error uploading renamed file");
    } finally {
      setIsLoading(false);
    }
  };

  // Extract common file processing logic into a reusable function
  const processFileUpload = async (
    fileToProcess: File,
    skipHashCheck: boolean = false
  ) => {
    setErrorMessage(null);

    if (fileToProcess.size > 10 * 1024 * 1024) {
      setErrorMessage("File is too large. Maximum size is 10MB.");
      return false;
    }

    if (
      ![
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain"
      ].includes(fileToProcess.type)
    ) {
      setErrorMessage(
        "Invalid file type. Only PDF, DOC, DOCX, and TXT files are supported."
      );
      return false;
    }

    try {
      const base64_file: string = await getBase64(fileToProcess);
      setIsLoading(true);

      // Check file hash if not skipping hash check
      if (!skipHashCheck) {
        const check_file_hash = await apiRequest<any>({
          subRoute: "ai",
          path: `/check_file_hash`,
          method: "POST",
          body: {
            base64_file: base64_file
          }
        });

        if (check_file_hash.length > 0) {
          trackEvent("same_hash_file_upload", {
            hash: check_file_hash,
            fileName: fileToProcess.name,
            fileSize: fileToProcess.size,
            fileType: fileToProcess.type
          });
          setSameHashModalFiles(check_file_hash);
          setSameHashModalOpen(true);
          setIsLoading(false);
          return false;
        }
      }

      // Check if file name already exists
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileToProcess.name)}`,
        method: "GET"
      });

      if (check_file_name.length > 0) {
        trackEvent("same_name_file_upload", {
          fileName: fileToProcess.name,
          fileType: fileToProcess.type,
          fileSize: fileToProcess.size
        });
        setSameNameModalOpen(true);
        setNewFileName(fileToProcess.name); // Initialize with current name for editing
        setIsLoading(false);
        return false;
      }

      // Upload the file
      const data = await apiRequest<{
        gcs_path: string;
        inserted_id: string;
      }>({
        subRoute: "ai",
        path: `/upload_file`,
        method: "POST",
        body: {
          base64_file: base64_file,
          file_type: fileToProcess.type,
          file_name: fileToProcess.name
        }
      });

      setGcsPath(data.gcs_path);
      fetchSources(); // Refresh the list after successful upload
      return true;
    } catch (error) {
      trackError("error_during_file_upload", error, {
        fileName: fileToProcess.name,
        fileSize: fileToProcess.size,
        fileType: fileToProcess.type
      });

      setErrorMessage("Failed to upload file. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e: any) => {
    // Check if there are files selected
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    let selectedFile = e.target.files[0] as File;
    setFile(selectedFile);

    try {
      await processFileUpload(selectedFile, ignoreSameHash);
    } finally {
      setIgnoreSameHash(false);
      // Reset the file input
      const fileInput = document.getElementById(
        "fileInput"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBorderColor: "rgb(64, 41, 117)", // 20 pxs lighter
            defaultBorderColor: "rgb(44, 21, 97)",
            defaultBg: "rgb(44, 21, 97)",
            defaultHoverBg: "rgb(64, 41, 117)" // 20 pxs lighter
          }
        }
      }}
    >
      <h3>Manage Sources</h3>

      {/* Create Note Modal */}
      <CreateNoteModal
        isOpen={createNoteHook.isModalOpen}
        noteName={createNoteHook.noteName}
        noteContent={createNoteHook.noteContent}
        isCreating={createNoteHook.isCreating}
        error={createNoteHook.error}
        isNameTaken={createNoteHook.isNameTaken}
        onClose={createNoteHook.closeModal}
        onNameChange={createNoteHook.handleNameChange}
        onContentChange={createNoteHook.setNoteContent}
        onCreate={createNoteHook.createNote}
      />
      {isShareModalVisible && (
        <ShareAccessModal
          refId={selectedSource.id}
          refType="source"
          onClose={() => setIsShareModalVisible(false)}
        />
      )}
      <Modal
        className="w-[400px]"
        open={isDeleteModalVisible}
        closable={true}
        onCancel={() => setIsDeleteModalVisible(false)}
        footer={[
          <Button
            key="back"
            type="default"
            style={{ color: "white" }}
            onClick={() => setIsDeleteModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="default"
            style={{ color: "white" }}
            loading={deleteLoading}
            onClick={() => {
              if (selectedRowKeys.length > 0) {
                handleBulkDelete();
              } else {
                handleDelete();
              }
            }}
          >
            Delete
          </Button>
        ]}
      >
        <p className="font-bold">
          {selectedRowKeys.length > 0
            ? `${selectedRowKeys.length} source${selectedRowKeys.length > 1 ? "s" : ""}`
            : selectedSource?.name}
        </p>
        <p>
          {selectedRowKeys.length > 0
            ? `Are you sure you want to delete ${selectedRowKeys.length} source${selectedRowKeys.length > 1 ? "s" : ""}?`
            : "Are you sure you want to delete this source?"}
        </p>
      </Modal>

      <input
        id="fileInput"
        accept=".pdf,.doc,.docx,.txt"
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "16px",
          height: "50px"
        }}
      >
        <Button
          onClick={() => {
            document.getElementById("fileInput").click();
          }}
          style={{ color: "white", width: "200px" }}
          type="default"
          loading={isLoading}
        >
          {isLoading ? "Uploading..." : "+ Upload new document"}
        </Button>
        <Button
          onClick={createNoteHook.openModal}
          style={{ color: "white", width: "200px" }}
          type="default"
        >
          + Create Note
        </Button>
        {selectedRowKeys.length > 0 && (
          <Button
            danger
            onClick={() => {
              setIsDeleteModalVisible(true);
            }}
            style={{ width: "200px" }}
          >
            Delete {selectedRowKeys.length} source
            {selectedRowKeys.length > 1 ? "s" : ""}
          </Button>
        )}
        <Input.Search
          placeholder="Search sources..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={(value) => setSearchTerm(value)}
          allowClear
          style={{ width: 300 }}
          className="custom-search-input"
        />
        <style>{`
            .custom-search-input .ant-input-search-button {
              background-color: white !important;
              border-color: #d9d9d9 !important;
            }
            .custom-search-input .ant-input-search-button:hover {
              background-color: white !important;
              border-color: #d9d9d9 !important;
            }
            .custom-search-input .ant-btn-icon {
              color: #000000 !important;
            }
          `}</style>

        <div
          style={{
            width: "500px",
            marginLeft: "50px"
          }}
        >
          <div style={{ width: "500px" }}>
            {errorMessage && (
              <Alert
                message={errorMessage}
                type="error"
                closable
                afterClose={() => setErrorMessage(null)}
                // Auto-close after 15 seconds
                className="auto-close-alert"
              />
            )}
            {gcsPath && (
              <Alert
                message="File uploaded successfully!"
                type="success"
                closable
                afterClose={() => {
                  setGcsPath(null);
                  fetchSources(); // Refresh the sources list after successful upload
                }}
                // Auto-close after 15 seconds
                className="auto-close-alert"
              />
            )}
          </div>
        </div>
      </div>
      {sameNameModalOpen && (
        <Modal
          open={true}
          onCancel={() => {
            setSameNameModalOpen(false);
            setNewFileName(null);
            setIsNewFileNameTaken(false);
            setErrorMessage(null);
            setIgnoreSameHash(false); // Reset ignore same hash flag
          }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setSameNameModalOpen(false);
                setNewFileName(null);
                setIsNewFileNameTaken(false);
                setErrorMessage(null);
                setIgnoreSameHash(false); // Reset ignore same hash flag
              }}
              style={{
                color: "white"
              }}
            >
              Cancel
            </Button>,
            <Tooltip
              title={
                !newFileName || newFileName.trim() === ""
                  ? "Please enter a new file name"
                  : newFileName === file?.name
                    ? "Please choose a different name than the original"
                    : isNewFileNameTaken
                      ? "This filename is already taken"
                      : ""
              }
            >
              <Button
                key="submit"
                loading={isLoading}
                disabled={
                  !newFileName ||
                  newFileName.trim() === "" ||
                  newFileName === file?.name ||
                  isNewFileNameTaken
                }
                onClick={handleRenameAndUpload}
                style={{
                  color:
                    !newFileName ||
                    newFileName.trim() === "" ||
                    newFileName === file?.name ||
                    isNewFileNameTaken
                      ? "black"
                      : "white"
                }}
              >
                Upload with new name
              </Button>
            </Tooltip>
          ]}
        >
          <p className="font-bold">
            File name already exists! Please rename your file.
          </p>
          <p>Enter a new name (extension will be preserved automatically):</p>
          <Input
            placeholder="New file name without extension"
            value={
              newFileName
                ? newFileName.includes(".")
                  ? newFileName.substring(0, newFileName.lastIndexOf("."))
                  : newFileName
                : file?.name
                  ? file.name.substring(0, file.name.lastIndexOf("."))
                  : ""
            }
            onChange={async (e) => {
              const value = e.target.value;
              setNewFileName(value);

              // Reset the error state when user starts typing
              if (isNewFileNameTaken) {
                setIsNewFileNameTaken(false);
                setErrorMessage(null);
              }

              // Add extension to the value for checking
              const valueWithExt = file
                ? ensureFileExtension(value, file.name)
                : value;

              // Check if the new name is already taken (debounced)
              if (value && value.trim() !== "" && valueWithExt !== file?.name) {
                // Clear any previous timeout
                if (checkFileNameTimeoutRef.current) {
                  clearTimeout(checkFileNameTimeoutRef.current);
                }

                // Set a new timeout to check the file name after typing stops
                checkFileNameTimeoutRef.current = setTimeout(async () => {
                  // Check with the extension added
                  const valueWithExt = file
                    ? ensureFileExtension(value, file.name)
                    : value;
                  const exists = await checkFileNameExists(valueWithExt);
                  setIsNewFileNameTaken(exists);
                  if (exists) {
                    setErrorMessage(
                      `The name "${valueWithExt}" is already taken. Please choose another name.`
                    );
                  }
                }, 500); // 500ms debounce
              }
            }}
          />
          {isNewFileNameTaken && (
            <div style={{ color: "red", marginTop: "8px" }}>
              This filename is already taken. Please choose another name.
            </div>
          )}
        </Modal>
      )}

      {sameHashModalOpen && (
        <Modal
          open={true}
          onCancel={() => {
            setSameHashModalOpen(false);
            setIgnoreSameHash(false);
          }}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setSameHashModalOpen(false);
                setIgnoreSameHash(false);
              }}
              style={{
                color: "white"
              }}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              loading={isLoading}
              onClick={() => {
                setIgnoreSameHash(true);
                setSameHashModalOpen(false);
                // Restart the upload process with the current file
                if (file) {
                  processFileUpload(file, true); // Skip hash check since we're continuing after hash warning
                }
              }}
              style={{
                color: "white"
              }}
            >
              Continue
            </Button>
          ]}
        >
          <p className="font-bold">File content matches the below file(s)!</p>
          {sameHashModalFiles.map((file, i) => (
            <p key={i}>{file.file_name}</p>
          ))}
          <p className="font-bold mt-10">
            Are you sure you want to continue uploading this file?
          </p>
        </Modal>
      )}

      <Table
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (
            selectedRowKeys: React.Key[],
            selectedRows: DataType[]
          ) => {
            console.log(
              `selectedRowKeys: ${selectedRowKeys}`,
              "selectedRows: ",
              selectedRows
            );
            setSelectedRowKeys(selectedRowKeys);
          }
        }}
        columns={columns}
        dataSource={ownedSources || []}
        scroll={{ y: "calc(65vh - 50px)" }}
        pagination={false}
        className="mt-10"
        sticky={{ offsetHeader: 64 }}
        loading={sources === null}
        locale={{
          emptyText: sources === null ? "Loading..." : "No documents found"
        }}
      />
      {sharedSources?.length > 0 && (
        <>
          <h3>Shared Sources</h3>
          <Table
            columns={columns}
            dataSource={sharedSources}
            scroll={{ y: "calc(65vh - 50px)" }}
            pagination={false}
            className="mt-10"
            sticky={{ offsetHeader: 64 }}
          />
        </>
      )}
    </ConfigProvider>
  );
}
